import { FCX, ID } from "@datassential/platform-ui-lib";
import { IOption } from "@models";
import React, { useCallback, useMemo } from "react";
import { getSvgPath, Point } from "@core/utils/chart";
import { Series } from "@components/GroupsColumnsLineChart/GroupsColumnsLineChart";
import cn from "classnames";

const Curves: FCX<{
    series: Series[];
    groups: IOption[];
    columns: IOption[];
    labels: number[];
    height: number;
    width: number;
    activeSeriesId: ID;
    activeGroupId: ID;
    activeColumnId: ID;
}> = ({
    series,
    groups,
    columns,
    activeGroupId,
    activeColumnId,
    labels,
    height,
    width,
    activeSeriesId,
}) => {
    const columnsCount = useMemo(
        () => groups.length * columns.length,
        [groups.length, columns.length]
    );
    const columnHalf = useMemo(
        () => width / (2 * columnsCount),
        [width, columnsCount]
    );
    const minValue = useMemo(
        (): number => labels[labels.length - 1],
        [labels]
    );
    const maxValue = useMemo(
        (): number => labels[0],
        [labels]
    );
    const getX = useCallback(
        (index: number): number => columnHalf * (2 * index + 1),
        [columnHalf]
    );
    const getY = useCallback(
        (value: number): number => (height) * (1 - (value - minValue) / (maxValue - minValue)),
        [height, maxValue, minValue]
    );

    const seriesCurves = useMemo(
        () => {
            return series.map((s, index) => {
                const points: Array<Point & { groupId: ID; columnId: ID; }> = [];

                groups.forEach((g, groupIndex) => {
                    columns.forEach((c, columnIndex) => {
                        const value = s.values.find(v => v.groupId === g.id && v.columnId === c.id);

                        if (value && value.value !== null) {
                            points.push({
                                x: getX(groupIndex * columns.length + columnIndex),
                                y: getY(value.value),
                                groupId: g.id,
                                columnId: c.id,
                            });
                        }
                    });
                });

                const totalPossiblePoints = groups.length * columns.length;

                return (
                    <g
                        key={`${s.id}`}
                        className={
                            cn(
                                "GroupsColumnsLineChart__curve-container",
                                {
                                    'is-dimmed': activeSeriesId !== null && activeSeriesId !== s.id,
                                }
                            )
                        }
                    >
                        <path
                            fill="none"
                            style={{
                                zIndex: activeSeriesId !== null && activeSeriesId === s.id ? 2 : undefined,
                                '--animation-duration': `${30 * totalPossiblePoints}ms`,
                                '--stroke-dashoffset': `-${30 * totalPossiblePoints}`,
                            } as React.CSSProperties}
                            stroke={s.color}
                            d={getSvgPath(points)}
                            className="GroupsColumnsLineChart__curve"
                            data-testid={`blockLineChartCurve_${index}`}
                        />
                        {points.map(((point, pIndex) => (
                            <circle
                                key={`${s.id}-${point.groupId}-${point.columnId}`}
                                cx={point.x}
                                cy={point.y}
                                // r={getRem(4)}
                                style={{ color: s.color, animationDelay: `${30 * totalPossiblePoints}ms` }}
                                stroke={s.color}
                                className={cn(
                                    "GroupsColumnsLineChart__dot",
                                    "appears",
                                    "fast",
                                    {
                                        'is-active': point.groupId === activeGroupId && point.columnId === activeColumnId,
                                    }
                                )}
                                data-testid={`blockLineChartDot_${index}_${pIndex}`}
                            />
                        )))}
                    </g>
                );
            })
        },
        [series, groups, columns, getX, getY, activeSeriesId, activeGroupId, activeColumnId]
    );

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
        >
            {seriesCurves}
        </svg>
    );
};

export default Curves;